import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { InputField } from "@/components/common/input-field";

interface SuggestCustomAgentDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (title: string, content: string, agentIds?: string[]) => void;
}

const AGENT_NAME_MAX = 50;
const REQUIREMENTS_MAX = 500;
const PRE_FILLED_AGENT_NAME = "적절한 에이전트가 없습니다";

export function SuggestCustomAgentDialog({
  open,
  onClose,
  onSubmit,
}: SuggestCustomAgentDialogProps) {
  const [agentName, setAgentName] = useState(PRE_FILLED_AGENT_NAME);
  const [requirements, setRequirements] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const isSubmitDisabled = !agentName.trim() || !requirements.trim() || isLoading;

  const handleSubmit = async () => {
    const trimmedName = agentName.trim();
    const trimmedRequirements = requirements.trim();
    if (!trimmedName || !trimmedRequirements) return;

    setIsLoading(true);
    try {
      await onSubmit(trimmedName, trimmedRequirements);
      setAgentName(PRE_FILLED_AGENT_NAME);
      setRequirements("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setAgentName(PRE_FILLED_AGENT_NAME);
    setRequirements("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="max-w-[350px] lg:max-w-[700px] w-[calc(100%-32px)] rounded-3xl p-6 gap-6
          shadow-[0px_8px_16px_0px_rgba(0,0,0,0.09),0px_0px_36px_0px_rgba(0,0,0,0.11)]
          [&>button_svg]:w-6 [&>button_svg]:h-6"
      >
        {/* Title */}
        <h2 className="text-2xl font-bold leading-7 md:leading-8 tracking-[-0.02em] text-[#202020] pr-8">
          다른 에이전트 요청
        </h2>

        {/* Form */}
        <div className="flex flex-col gap-4">
          {/* Agent Name Input */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="agent-name"
              className="text-sm font-normal text-[#202020] leading-5 tracking-[-0.01em]"
            >
              에이전트 이름
            </label>
            <InputField
              id="agent-name"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              placeholder="에이전트 이름 입력"
              maxLength={AGENT_NAME_MAX}
              className=" font-normal"
            />
          </div>

          {/* Requirements Textarea */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="requirements"
              className="text-sm font-normal text-[#202020] leading-5 tracking-[-0.01em]"
            >
              요구 사항
            </label>
            <div className="relative">
              <Textarea
                id="requirements"
                value={requirements}
                onChange={(e) => setRequirements(e.target.value)}
                placeholder="요구 사항 입력"
                maxLength={REQUIREMENTS_MAX}
                className="h-[88px] px-4 py-3 bg-black/[0.05] rounded-lg border-0 text-base text-[#202020] placeholder:text-[#8D8D8D] resize-none pr-10"
              />
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex gap-3">
          <Button
            variant="ghost"
            onClick={handleClose}
            className="h-12 flex-1 rounded-[1000px] bg-black/[0.05] text-[#202020] font-bold text-base hover:bg-black/[0.1]"
          >
            취소
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className="h-12 flex-1 rounded-[1000px] text-white font-bold text-base disabled:opacity-50"
          >
            등록
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
